import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Gamepad2, Target, Zap, Trophy, Brain, Clock, Star } from 'lucide-react';

export default function GameStrategies() {
  const navigate = useNavigate();

  return (
    <div 
      className="min-h-screen p-3 sm:p-4 md:p-6 relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <Button
          onClick={() => navigate('/start')}
          variant="ghost"
          className="mb-6 text-white hover:bg-white/20"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Game
        </Button>

        <Card className="bg-white/95 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-2xl">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 mb-6 sm:mb-8">
            <div className="p-2 sm:p-3 md:p-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl sm:rounded-2xl flex-shrink-0">
              <Gamepad2 className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#9370DB] to-[#FFD700] bg-clip-text text-transparent leading-tight">
              Game Strategies & Tips
            </h1>
          </div>

          <div className="prose prose-sm sm:prose-base max-w-none space-y-6 sm:space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-lg text-gray-700 leading-relaxed">
                Welcome to the ultimate strategy guide for Mega Church Game! Whether you're aiming to 
                reach the top of the leaderboard or simply want to maximize your members and unlock all levels, 
                these proven strategies will help you succeed. This comprehensive guide covers everything from 
                beginner tips to advanced tactics for mastering all six levels.
              </p>
            </section>

            {/* Understanding the Game Structure */}
            <section className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-6 h-6 text-purple-600" />
                <h2 className="text-3xl font-bold text-gray-800">Understanding the Game Structure</h2>
              </div>
              <p className="text-gray-700 mb-4">
                Before diving into strategies, it's crucial to understand how the game works:
              </p>
              <div className="space-y-3 text-gray-700">
                <div className="bg-white p-4 rounded-xl">
                  <h3 className="font-semibold text-blue-600 mb-2">Free Levels (1-3)</h3>
                  <p>
                    • <strong>Level 1:</strong> 5 questions, 10 members per correct answer (max 50 members)<br/>
                    • <strong>Level 2:</strong> 5 questions, 30 members per correct answer (max 150 members)<br/>
                    • <strong>Level 3:</strong> 10 questions, 30 members per correct answer (max 300 members)<br/>
                    • <strong>Lock Condition:</strong> These levels lock when you reach 2,000 total members
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl">
                  <h3 className="font-semibold text-yellow-600 mb-2">Premium Levels (4-6)</h3>
                  <p>
                    • <strong>Level 4:</strong> 15 questions, 50 members per correct answer (max 750 members)<br/>
                    • <strong>Level 5:</strong> 15 questions, 50 members per correct answer (max 750 members)<br/>
                    • <strong>Level 6:</strong> 20 questions, 50 members per correct answer (max 1,000 members)<br/>
                    • <strong>Unlock Cost:</strong> One-time payment of $3.00
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl">
                  <h3 className="font-semibold text-green-600 mb-2">Win Condition</h3>
                  <p>
                    Reach <strong>15,000 total members</strong> to achieve Mega Status and win the game! 
                    Milestone celebrations appear at 5,000, 10,000, and 15,000 members.
                  </p>
                </div>
              </div>
            </section>

            {/* Level-by-Level Strategies */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Star className="w-6 h-6 text-yellow-600" />
                <h2 className="text-3xl font-bold text-gray-800">Level-by-Level Strategies</h2>
              </div>

              {/* Level 1 Strategy */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl mb-4">
                <h3 className="text-2xl font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                  Level 1: Easy (Warm-Up Level)
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Difficulty:</strong> Easy biblical knowledge (names, basic stories)</p>
                  <p><strong>Strategy:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Use this as a warm-up to get familiar with the question format</li>
                    <li>Take your time—these questions are straightforward</li>
                    <li>Don't rush; accuracy is more important than speed at this stage</li>
                    <li>Expected score: 100% achievable with basic Bible knowledge</li>
                  </ul>
                  <p className="font-semibold text-green-700 mt-3">
                    💡 Pro Tip: Play Levels 1 - 3 multiple times early to build up members before the free tier locks at 2,000.
                  </p>
                </div>
              </div>

              {/* Level 2 Strategy */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl mb-4">
                <h3 className="text-2xl font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Level 2: Moderate (Member Multiplier)
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Difficulty:</strong> Moderate questions requiring more specific knowledge</p>
                  <p><strong>Key Advantage:</strong> 30 members per answer = 3x Level 1 reward!</p>
                  <p><strong>Strategy:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>This is your best value level for member accumulation (150 max vs. 50 in Level 1)</li>
                    <li>Play repeatedly to maximize members before hitting the 2,000 lock</li>
                    <li>Review Old Testament stories and New Testament parables before playing</li>
                    <li>If you miss a question, look up the answer afterward to improve next time</li>
                  </ul>
                  <p className="font-semibold text-blue-700 mt-3">
                    💡 Pro Tip: Focus on Level 2 for efficient member gain. Completing it 4 times = 600 members!
                  </p>
                </div>
              </div>

              {/* Level 3 Strategy */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-5 rounded-xl mb-4">
                <h3 className="text-2xl font-semibold text-purple-700 mb-3 flex items-center gap-2">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                  Level 3: Hard (Maximum Free Reward)
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Difficulty:</strong> Challenging questions on specific verses, prophecies, and details</p>
                  <p><strong>Key Advantage:</strong> Highest member gain in free tier (300 max)</p>
                  <p><strong>Strategy:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Requires solid biblical knowledge—study before attempting</li>
                    <li>Focus on prophets, kings, epistles, and specific verse references</li>
                    <li>Read questions carefully; many include subtle details</li>
                    <li>Use all 15 seconds per question to think through answers</li>
                    <li>Don't guess—accuracy matters more than speed</li>
                  </ul>
                  <p className="font-semibold text-purple-700 mt-3">
                    💡 Pro Tip: Master Level 3 before free tiers lock. Even 2-3 perfect runs = 600-900 members!
                  </p>
                </div>
              </div>

              {/* Premium Levels Strategy */}
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-5 rounded-xl border-2 border-yellow-400">
                <h3 className="text-2xl font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-yellow-600" />
                  Premium Levels (4-6): Advanced Tactics
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Investment:</strong> $3.00 one-time payment for permanent access</p>
                  <p><strong>Value:</strong> 50 members per answer = 5x Level 1!</p>
                  <p><strong>Strategy:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Level 4 (Minister):</strong> 15 questions = 750 members max. Focus on epistles and early church history</li>
                    <li><strong>Level 5 (Pastor):</strong> 15 questions = 750 members max. Covers prophecy, revelation, and theological concepts</li>
                    <li><strong>Level 6 (Apostle):</strong> 20 questions = 1,000 members max. Most challenging content across all Scripture</li>
                  </ul>
                  <p className="font-semibold text-yellow-700 mt-3">
                    💡 Pro Tip: Premium levels are essential for reaching 15,000 members. Plan to invest if you're serious about winning!
                  </p>
                </div>
              </div>
            </section>

            {/* Advanced Strategies */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-6 h-6 text-indigo-600" />
                <h2 className="text-3xl font-bold text-gray-800">Advanced Strategies</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-5 rounded-xl">
                  <Clock className="w-8 h-8 text-indigo-600 mb-3" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Time Management</h3>
                  <p className="text-gray-700">
                    You have 15 seconds per question. Use the first 5 seconds to read carefully, 5 seconds to 
                    eliminate wrong answers, and 5 seconds to select your final answer. Never rush—accuracy 
                    beats speed in this game.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-pink-50 to-red-50 p-5 rounded-xl">
                  <Zap className="w-8 h-8 text-pink-600 mb-3" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Question Pattern Recognition</h3>
                  <p className="text-gray-700">
                    After playing multiple times, you'll notice patterns in question types. True/False 
                    questions often test specific details, while multiple choice may test broader concepts. 
                    Learn from each playthrough.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl">
                  <Target className="w-8 h-8 text-green-600 mb-3" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Strategic Replays</h3>
                  <p className="text-gray-700">
                    Focus on replaying levels where you scored below 100%. Each replay is an opportunity to 
                    earn more members AND improve your biblical knowledge. Aim for perfect scores on all levels.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-5 rounded-xl">
                  <Star className="w-8 h-8 text-purple-600 mb-3" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Leaderboard Climbing</h3>
                  <p className="text-gray-700">
                    To reach the top of the leaderboard, you need consistent high scores across ALL levels. 
                    Premium players with multiple perfect runs on Levels 4-6 dominate the rankings. Consistency 
                    is key!
                  </p>
                </div>
              </div>
            </section>

            {/* Optimal Play Path */}
            <section className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-2xl">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Optimal Play Path to 15,000 Members</h2>
              <div className="space-y-3 text-gray-700">
                <div className="bg-white p-4 rounded-xl">
                  <h3 className="font-semibold text-blue-600 mb-2">Phase 1: Free Tier Mastery (0-2,000 members)</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Play Level 1 twice to get comfortable (100 members)</li>
                    <li>Focus on Level 2 - play 5 times for 750 members (850 total)</li>
                    <li>Master Level 3 - play 4 times for 1,200 members (2,050 total → locks free tiers)</li>
                  </ol>
                  <p className="text-sm mt-2 italic">Time investment: 2-3 hours of focused play</p>
                </div>
                <div className="bg-white p-4 rounded-xl">
                  <h3 className="font-semibold text-yellow-600 mb-2">Phase 2: Premium Investment (2,000-15,000 members)</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Unlock Premium Levels ($3.00 one-time payment)</li>
                    <li>Play Level 4 eight times for 6,000 members (8,050 total)</li>
                    <li>Play Level 5 eight times for 6,000 members (14,050 total)</li>
                    <li>Play Level 6 once for 1,000+ members (15,000+ total → WIN!)</li>
                  </ol>
                  <p className="text-sm mt-2 italic">Time investment: 4-6 hours total for levels 4-6</p>
                </div>
                <p className="font-semibold text-green-600 bg-green-50 p-3 rounded-lg">
                  ✅ Total time to victory: 6-9 hours of strategic gameplay + $3.00 premium unlock
                </p>
              </div>
            </section>

            {/* Common Mistakes to Avoid */}
            <section className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-2xl">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Common Mistakes to Avoid</h2>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start gap-3">
                  <span className="text-red-600 font-bold text-xl">✗</span>
                  <div>
                    <h3 className="font-semibold text-gray-800">Rushing Through Questions</h3>
                    <p>Taking your time and reading carefully prevents silly mistakes that cost you members.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-red-600 font-bold text-xl">✗</span>
                  <div>
                    <h3 className="font-semibold text-gray-800">Ignoring Level 2 and 3</h3>
                    <p>These offer the best member-to-time ratio in the free tier. Don't skip them!</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-red-600 font-bold text-xl">✗</span>
                  <div>
                    <h3 className="font-semibold text-gray-800">Not Studying Between Attempts</h3>
                    <p>Review incorrect answers and study those topics before replaying levels.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-red-600 font-bold text-xl">✗</span>
                  <div>
                    <h3 className="font-semibold text-gray-800">Delaying Premium Purchase</h3>
                    <p>If you're aiming for 15,000 members, you'll need premium levels. Unlock early to maximize value.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Ready to Dominate?</h2>
              <p className="text-gray-700 mb-4">
                With these strategies in hand, you're equipped to maximize your performance in Mega Church Game. 
                Remember that the game is designed to test AND teach biblical knowledge. Every question is an 
                opportunity to learn something new about Scripture.
              </p>
              <p className="text-gray-700 mb-4">
                Combine these game strategies with regular Bible study (check out our Bible Study Tips page!) 
                for the best results. The players at the top of the leaderboard aren't just good gamers—they're 
                dedicated students of God's Word.
              </p>
              <p className="text-gray-700 font-semibold">
                May your tribe increase and your knowledge of Scripture grow with every level! 🏆
              </p>
            </section>

            {/* Call to Action */}
            <div className="text-center pt-8 border-t-2 border-gray-200">
              <Button
                onClick={() => navigate('/level-select')}
                className="bg-gradient-to-r from-[#9370DB] to-[#FFD700] text-white px-8 py-6 text-lg rounded-2xl hover:shadow-2xl transition-all"
              >
                Start Playing Now!
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
